import { FC, useEffect, useMemo, useRef, useState } from 'react';
import cs from 'classnames';
import { useMethods } from '@lhb/hook';
import { downloadFile, each, isNotEmpty, stringExtension } from '@lhb/func';
import { officeView, pdfView, specialDownloadTypes, typeMap } from '@/common/components/config-v2';
import { IMAGE_TYPES, VIDEO_TYPES } from 'src/common/utils/file-types';
import styles from './index.module.less';
import { Modal, Button, Image, Typography } from 'antd';
import IconFont from 'src/common/components/Base/IconFont/index';
import Container from 'src/common/components/Container/index';
import VideoExampleItem from './VideoExampleItem';

/** 单个素材文件 */
type Material = { name: string; url: string; suffix: string; previewHref?: string; };

/** 单组示例 */
type ExampleItem = {
  id: string;
  materials: Material[];
  caption: string;
};

/** 图片 / 文件 示例配置（结构一致） */
interface ImgConfig {
  examples: ExampleItem[];
  rights: string;
  errors: string;
}

/** 视频示例配置（扩展时长与拍摄方式） */
interface VideoConfig extends ImgConfig {
  videoMinDuration: number;
  videoMaxDuration: number;
  shootMethod: string;
}

/** 上传类型 */
type UploadType = 'image' | 'video' | 'file';

/** 组件 Props */
interface UploadFileExamplesProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 关闭弹窗回调 */
  onClose: () => void;
  /** 上传类型，决定渲染哪种素材区 */
  uploadType: UploadType;
  /** 弹窗标题文案（已在外部按类型拼好） */
  title: string;
  /** 示例配置对象 */
  config: ImgConfig | VideoConfig | null;
}

/** 标题左侧 emoji 映射（图2/3 视觉风格） */
const TITLE_EMOJI_MAP: Record<UploadType, string> = {
  image: '📷',
  video: '🎬',
  file: '📎',
};

/** caption / 文件名 文本超出鼠标悬浮 Tooltip 共用配置 */
export const TEXT_TOOLTIP_INNER_STYLE = {
  maxHeight: '180px',
  overflowY: 'auto' as const,
};

/**
 * 根据文件后缀返回适合"在新标签预览"的链接
 * @description 移植自 V2DetailItem 的 refactorUrl 实现：
 * - 图片：返回 undefined（交由 antd Image 内置预览）
 * - 视频 / Office / PDF：分别走对应静态资源预览页
 * - 特殊下载类型（如 dwg）：拼 attname 强制下载
 * - 其它未识别后缀：直接返回原 url
 * @param {string} url 文件 URL
 * @param {string} name 文件名（用于预览页参数）
 * @param {string} suffix 文件后缀（小写、无点号）
 * @returns {string | undefined} 浏览器可直接打开的预览 URL
 */
const refactorUrl = (url: string, name: string, suffix: string): string | undefined => {
  if (IMAGE_TYPES.includes(suffix)) {
    // 图片不走新标签预览，靠 antd Image 在弹窗内预览
    return undefined;
  } else if (VIDEO_TYPES.includes(suffix)) {
    // 视频预览页
    let targetUrl = `${process.env.STATIC_RES_DOMAIN}/libs/preview-video/index.html?rotation=1&url=${url}`;
    if (name) {
      targetUrl += `&filename=${encodeURIComponent(name)}`;
    }
    return targetUrl;
  } else if (officeView.includes(suffix)) {
    // Office 文档预览页
    let targetUrl = `${process.env.STATIC_RES_DOMAIN}/libs/preview-office/index.html?url=${url}&office=1`;
    if (name) {
      targetUrl += `&filename=${encodeURIComponent(name)}`;
    }
    return targetUrl;
  } else if (pdfView.includes(suffix)) {
    // PDF 预览页
    return `${process.env.STATIC_RES_DOMAIN}/libs/preview-pdf/web/viewer.html?file=${url}` + encodeURIComponent(`#${name}`);
  } else if (specialDownloadTypes.includes(suffix)) {
    // dwg 等特殊类型：拼 attname 走下载
    return `${url}?attname=${encodeURIComponent(name)}`;
  }
  return url;
};

/**
 * 获取文件图标
 * @returns {string} 文件图标
 */
const getFileIcon = (name: string): string => {
  let hrefVal = `iconfile_icon_unknow`;
  const suffix = (stringExtension(name) || '').toLowerCase().replace(/^\./, '');
  each(typeMap, (itm: any, key: string) => {
    if (suffix && key.split(',').includes(suffix)) {
      hrefVal = itm;
    }
  });
  return hrefVal;
};

/**
 * 上传文件的示例
 * @description 根据 uploadType 渲染三种示例：
 *  - image：图片网格 + 底部 caption 浮层
 *  - video：视频播放器 + 建议时长 / 拍摄方式 信息卡
 *  - file：文件卡片列表 + 预览（按文件类型走对应预览页）+ 下载按钮
 * 数据结构示例（保留作为开发参考）：
 * imgExampleConfig = {
 *   examples: [{ id, materials: [{ name, url }], caption }],
 *   rights: '\\n 分隔的正确要点',
 *   errors: '\\n 分隔的常见错误'
 * }
 * videoExampleConfig = {
 *   ...imgExampleConfig,
 *   videoMinDuration, videoMaxDuration, shootMethod
 * }
 * fileExampleConfig = { ...imgExampleConfig }
 */
const Component: FC<UploadFileExamplesProps> = ({
  open,
  onClose,
  uploadType,
  title,
  config,
}) => {

  // 兼容 config 为 null 的边界场景，统一兜底解构
  const rights: string = config?.rights || '';
  const errors: string = config?.errors || '';
  const videoCfg = config as VideoConfig | null;

  const examples = useMemo(() => {
    return config && Array.isArray(config.examples) ? config.examples.map(item => ({
      ...item,
      materials: Array.isArray(item.materials) ? item.materials.map(m => {
        const suffix = stringExtension(m.name).toLowerCase().replace('.', '');
        return {
          ...m,
          suffix,
          previewHref: refactorUrl(m.url, m.name, suffix),
          fileIcon: getFileIcon(m.name),
        };
      }) : []
    })) : [];
  }, [config]);

  const [prevImage, setPrevImage] = useState<any>({
    previewVisible: false,
    previewImage: '',
    previewTitle: '',
  });

  /** 视频示例项 ref */
  const videoExampleItemRef = useRef<any[]>([]);

  /** 容器 ref */
  const containerRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        containerRef.current.scrollTop?.();
      }, 0);
    } else {
      // 关闭弹窗时主动暂停所有示例视频
      if (videoExampleItemRef && Array.isArray(videoExampleItemRef.current) && videoExampleItemRef.current.length > 0) {
        videoExampleItemRef.current.forEach(el => {
          el?.pause?.();
        });
      }
    }
  }, [open]);

  const methods = useMethods({

    /* 按上传类型选择素材区渲染函数 */
    renderMaterials: () => {
      if (uploadType === 'image') return methods.renderImages();
      if (uploadType === 'video') return methods.renderVideos();
      return methods.renderFiles();
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      onClose();
    },

    /** 预览示例文件 */
    handlePreview(material: Material) {
      if (IMAGE_TYPES.includes(material.suffix)) { // 图片预览
        setPrevImage(val => ({
          ...val,
          previewImage: material.url,
          previewVisible: true,
          previewTitle: material.name,
        }));
        return;
      }
      if (!material.previewHref) return;
      window.open(material.previewHref, '_blank');
    },

    handlePreviewImageCancel() {
      setPrevImage(val => ({ ...val, previewVisible: false, previewImage: '', previewTitle: '' }));
    },

    /**
     * 下载示例文件
     * @param {Material} material 文件素材对象
     */
    handleDownload(material: Material) {
      if (!material.url) return;
      downloadFile({ url: material.url, name: material.name });
    },
    /**
     * 将 \n 分隔的字符串拆为非空行数组
     * @param {string} text 原始字符串
     * @returns {string[]} 去除前后空白的非空行
     */
    splitLines(text: string): string[] {
      return (text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => isNotEmpty(line));
    },
    /**
     * 渲染图片示例区域
     * @returns {React.ReactNode} 图片网格 JSX
     */
    renderImages() {
      return (
        <Image.PreviewGroup>
          <div className={styles.imgGrid}>
            {/* 平铺每个 example 下的所有 materials */}
            {examples.flatMap((ex) =>
              ex.materials.map((m) => (
                <div className={styles.imgItem} key={`${ex.id}-${m.url}`}>
                  <Image src={m.url} />
                  {/* caption 浮层：超长时鼠标悬浮显示完整文字 */}
                  {isNotEmpty(ex.caption) && <div className={styles.imgCaption}>
                    <Typography.Text
                      className={styles.imgCaptionText}
                      ellipsis={{
                        tooltip: {
                          title: ex.caption,
                          overlayInnerStyle: TEXT_TOOLTIP_INNER_STYLE,
                        },
                      }}
                    >
                      ✅ {ex.caption}
                    </Typography.Text>
                  </div>}
                </div>
              ))
            )}
          </div>
        </Image.PreviewGroup>
      );
    },
    /**
     * 渲染视频示例区域
     * @returns {React.ReactNode} 视频列表 + 信息卡 JSX
     */
    renderVideos() {
      return (
        <>
          <div className={styles.videoList}>
            {/* 每组视频依次堆叠展示（含时长展示） */}
            {examples.flatMap((ex) =>
              ex.materials.map((m) => (
                <VideoExampleItem
                  ref={el => videoExampleItemRef.current.push(el)}
                  key={`${ex.id}-${m.url}`}
                  src={m.url}
                  caption={ex.caption}
                />
              ))
            )}
          </div>
          {/* 信息卡：建议时长 + 拍摄方式 */}
          {(isNotEmpty(videoCfg?.videoMinDuration) && isNotEmpty(videoCfg?.videoMaxDuration)) || (isNotEmpty(videoCfg?.shootMethod)) ? <div className={styles.videoMetaRow}>
            {isNotEmpty(videoCfg?.videoMinDuration) && isNotEmpty(videoCfg?.videoMaxDuration) && <div className={styles.metaCard}>
              <IconFont iconHref='icon-clock1' />
              <div className={styles.metaLabel}>建议时长</div>
              <div className={cs(styles.metaValue, styles.videoDuration)}>
                {videoCfg?.videoMinDuration} - {videoCfg?.videoMaxDuration} 秒
              </div>
            </div>}
            {/* shootMethod 为空时不渲染该信息卡，避免空白 */}
            {isNotEmpty(videoCfg?.shootMethod) && (
              <div className={styles.metaCard}>
                <IconFont iconHref='iconic_video' />
                <div className={styles.metaLabel}>拍摄方式</div>
                <div className={cs(styles.metaValue, styles.shootMethod)}>{videoCfg?.shootMethod}</div>
              </div>
            )}
          </div> : <></>}
        </>
      );
    },
    /**
     * 渲染文件示例区域
     * @returns {React.ReactNode} 文件卡片列表 JSX
     */
    renderFiles() {
      return (
        <div className={styles.fileList}>
          {examples.flatMap((ex) =>
            ex.materials.map((m) => {
              return (
                <div className={styles.fileCard} key={`${ex.id}-${m.url}`}>
                  {/* 使用 emoji 作为文件类型占位图标，避免依赖额外资源 */}
                  <IconFont iconHref={m.fileIcon} className={styles.fileIcon} />
                  <div className={styles.fileInfo}>
                    {/* 文件名：超长时鼠标悬浮显示完整名称 */}
                    <Typography.Text
                      className={styles.fileName}
                      ellipsis={{
                        tooltip: {
                          title: m.name,
                          overlayInnerStyle: TEXT_TOOLTIP_INNER_STYLE,
                        },
                      }}
                    >
                      {m.name}
                    </Typography.Text>
                    {ex.caption && (
                      <Typography.Text
                        className={styles.fileCaption}
                        ellipsis={{
                          tooltip: {
                            title: ex.caption,
                            overlayInnerStyle: TEXT_TOOLTIP_INNER_STYLE,
                          },
                        }}
                      >
                        {ex.caption}
                      </Typography.Text>
                    )}
                  </div>
                  <div className={styles.fileActions}>
                    {/* 预览：复用 V2DetailItem 的 refactorUrl 策略，按文件类型走对应预览页 */}
                    <Button type='link' onClick={() => methods.handlePreview(m)}>预览</Button>
                    <Button type='link' onClick={() => methods.handleDownload(m)}>下载</Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      );
    },
  });

  /* 拆好的要点 / 错误行 */
  const rightLines = methods.splitLines(rights);
  const errorLines = methods.splitLines(errors);
  const showRightLines = rightLines.length > 0;
  const showErrorLines = errorLines.length > 0;
  const showTipsRow = showRightLines || showErrorLines;

  return (
    <>
      <Modal
        open={open}
        width={600}
        centered
        destroyOnClose={false}
        wrapClassName='lhb-upload-file-examples-modal'
        title={
          <div className={styles.titleBar}>
            <span className={styles.titleIcon}>{TITLE_EMOJI_MAP[uploadType]}</span>
            <span>{title}</span>
          </div>
        }
        footer={
          <div className={styles.footer}>
            <Button type='primary' onClick={methods.handleClose}>我知道了，去上传</Button>
          </div>
        }
        onCancel={methods.handleClose}
      >
        <Container ref={containerRef} style={{ maxHeight: '70vh' }}>
          {/* 素材区 */}
          {methods.renderMaterials()}

          {/* 正确要点 / 常见错误 对比区 */}
          {!!showTipsRow && <div className={styles.tipsRow}>
            {!!showRightLines && <div className={styles.rightsBox}>
              <div className={styles.boxTitle}>✅ 正确要点</div>
              <ul>
                {rightLines.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>}
            {!!showErrorLines && <div className={styles.errorsBox}>
              <div className={styles.boxTitle}>❌ 常见错误</div>
              <ul>
                {errorLines.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>}
          </div>}
        </Container>
      </Modal>

      {/* 文件类型时，预览图片 */}
      {prevImage.previewVisible && <div style={{ display: 'none' }}>
        <Image
          width={0}
          preview={{
            visible: prevImage.previewVisible,
            src: prevImage.previewImage,
            getContainer: () => document.body,
            onVisibleChange: () => {
              methods.handlePreviewImageCancel();
            },
          }}
        />
      </div>}

    </>
  );
};

export default Component;
